/**
 * AdminLoginLayout - Minimal layout za admin login stranu
 *
 * Tenant branding iznad forme, "Powered by BeClass Manager" ispod.
 * Short screens scroll so forgot/reset password fields stay reachable.
 */

"use client";

import { Box, Divider, Flex } from "@chakra-ui/react";
import { type PropsWithChildren } from "react";
import { AppLogo } from "@/components";
import { TenantLogo } from "@/components/shared/TenantLogo";
import { Link, Text, ThemeToggle } from "@/components/ui";
import { useTenantData, useTenantLogoUrl } from "@/hooks";
import { useThemeTokens } from "@/lib/theme/tokens";
import { hexToRgba } from "@/lib/utils/color";
import { InitialPersonalizationProvider } from "@/lib/theme/InitialPersonalizationContext";
import type { ColorPaletteId } from "@/lib/theme/color-palettes";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { HeroVisibilityProvider, useHeroVisibility } from "@/lib/contexts";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import { FULL_VIEWPORT_HEIGHT } from "@/lib/layout/viewport";
import { PublicGlassPanel } from "./PublicGlassPanel";
import { PublicPageHero } from "./PublicPageHero";
import { PUBLIC_AUTH_FORM_WIDTH, PUBLIC_CONTENT_SPACING } from "./public-layout-constants";

type AdminLoginLayoutProps = PropsWithChildren & {
  initialPersonalization?: {
    colorPalette: ColorPaletteId;
    customColor: string | null;
    grainEnabled: boolean;
  } | null;
};

const AdminLoginLayoutContent = ({ children }: PropsWithChildren) => {
  const theme = useThemeTokens();
  const { isHeroVisible } = useHeroVisibility();
  const tenantId = resolveEffectiveTenantNumber();
  const { tenantData } = useTenantData();
  const tenantLogoUrl = useTenantLogoUrl();
  const tenantName = getTenantDisplayName(tenantData);

  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: !!tenantId },
  );

  const heroContent = isHeroVisible
    ? {
        label: contentSettings?.adminSignInLabel ?? "",
        title: contentSettings?.adminSignInTitle ?? "",
        description: contentSettings?.adminSignInDescription ?? "",
      }
    : null;

  const showHero =
    heroContent != null &&
    Boolean(heroContent.label?.trim()) &&
    Boolean(heroContent.title?.trim());

  return (
    <Flex
      w="100%"
      minH={FULL_VIEWPORT_HEIGHT}
      direction="column"
      position="relative"
      bg={theme.bg.primary}
      overflowX="hidden"
      overflowY="auto"
    >
      {theme.gradientEnabled ? (
        <Box
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          pointerEvents="none"
          zIndex={0}
          sx={{
            background:
              theme.mode === "dark"
                ? `radial-gradient(ellipse 70% 45% at 50% 0%, ${hexToRgba(theme.accent.primary, 0.12)} 0%, transparent 55%),
                   linear-gradient(180deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.08) 45%, rgba(0,0,0,0.16) 100%)`
                : `radial-gradient(ellipse 70% 45% at 50% 0%, ${hexToRgba(theme.accent.primary, 0.06)} 0%, transparent 55%),
                   linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)`,
          }}
        />
      ) : null}

      {theme.mode === "dark" ? (
        <Box
          position="absolute"
          top="6%"
          left="50%"
          w="320px"
          h="320px"
          borderRadius="50%"
          bg={hexToRgba(theme.accent.primary, 0.09)}
          filter="blur(80px)"
          pointerEvents="none"
          zIndex={0}
          transform="translateX(-50%)"
        />
      ) : null}

      <Box
        display={{ base: "none", lg: "block" }}
        position="fixed"
        top="20px"
        right="20px"
        zIndex={11}
      >
        <ThemeToggle />
      </Box>

      <Flex
        flex="1"
        w="100%"
        minH={FULL_VIEWPORT_HEIGHT}
        position="relative"
        zIndex={1}
        px={PUBLIC_CONTENT_SPACING.px}
        py={{ base: 5, md: 6 }}
        align="center"
        justify="center"
        direction="column"
        gap={3}
      >
        <PublicGlassPanel
          maxW={PUBLIC_AUTH_FORM_WIDTH}
          p={{ base: 4, md: 5 }}
        >
          <Flex justify="center" mb={showHero ? 3 : 4}>
            {tenantLogoUrl ? (
              <TenantLogo
                width={64}
                height={64}
                tenantData={tenantData}
                src={tenantLogoUrl}
                alt={tenantName}
                priority
              />
            ) : (
              <Text
                fontSize="lg"
                fontWeight="600"
                letterSpacing="-0.02em"
                color={theme.text.primary}
                textAlign="center"
              >
                {tenantName}
              </Text>
            )}
          </Flex>

          {showHero ? (
            <>
              <Box textAlign={{ base: "left", md: "center" }} w="100%" mx="auto">
                <PublicPageHero {...heroContent} variant="compact" />
              </Box>
              <Divider
                my={4}
                borderColor={theme.borderGlass.subtle}
                opacity={0.7}
              />
            </>
          ) : null}

          {children}
        </PublicGlassPanel>

        <Flex align="center" justify="center" gap={2} w="100%" maxW={PUBLIC_AUTH_FORM_WIDTH}>
          <Text fontSize="xs" color={theme.text.tertiary}>
            Powered By
          </Text>
          <Link href="https://beclassmanager.com" external>
            <AppLogo h="28px" />
          </Link>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const AdminLoginLayout = ({
  children,
  initialPersonalization,
}: AdminLoginLayoutProps) => {
  return (
    <InitialPersonalizationProvider value={initialPersonalization}>
      <HeroVisibilityProvider>
        <AdminLoginLayoutContent>{children}</AdminLoginLayoutContent>
      </HeroVisibilityProvider>
    </InitialPersonalizationProvider>
  );
};
